// SIMS-9077 (PRD v0.3 FR-12, decision D1) — resolve how a digital fishbowl's draws are presented.
//
// Mirrors libs/shared/src/helpers/fishbowl-presentation.helper.ts in the backend. The rule is
// resolved on READ, not defaulted in the schema: AC2 requires a configuration saved before this
// release to show as the results list WITHOUT reconfiguration, which means the stored document
// must stay byte-identical until someone deliberately picks a presentation.

// Relative, not "@/domain/...": FishbowlPresentation is an ENUM, i.e. a runtime value, so webpack
// has to resolve this specifier. react-scripts does not honour tsconfig's "paths", so the other
// "@/" imports under src/utils survive only because they are type-only and TypeScript erases them
// before the bundler ever sees them. An "@/" import here breaks `yarn build` while leaving jest and
// tsc green, because both DO honour "paths".
import {
	FishbowlCustomConfig,
	FishbowlPresentation,
} from "../domain/reward.interface";

/**
 * Where the draws are being presented.
 *
 * SIMS-9076 — "entry" is a coordinator entering a new visit; "correction" is a coordinator
 * correcting one that is already recorded.
 */
export type FishbowlDrawContext = "entry" | "correction";

/**
 * The presentation a digital fishbowl configuration should be rendered with.
 *
 * Pure and total: never throws, never mutates its argument, always returns a presentation.
 * An absent or unrecognised value resolves to LIST — what every existing configuration does today.
 *
 * SIMS-9076 AC1 — a CORRECTION always uses the list, whatever the configuration says. The
 * presentation field is shared between the two paths, so without this rule a plan configured for
 * the game would start showing the game on the correction path the moment SIMS-9068 ships. That
 * is why this lands in THIS epic despite being a no-op until the game exists.
 *
 * @param config the saved rewardCustomConfig, which may be absent or pre-release (no presentation)
 * @param context defaults to "entry" — the path that HONOURS the configuration, so a caller that
 *   forgets to pass one gets the configured behaviour rather than a silently forced list
 */
export function resolveFishbowlPresentation(
	config?: Partial<FishbowlCustomConfig> | null,
	context: FishbowlDrawContext = "entry",
): FishbowlPresentation {
	// SIMS-9076 AC1 — unconditional, and deliberately BEFORE the field is read: the correction
	// path does not consult the configured presentation at all.
	if (context === "correction") {
		return FishbowlPresentation.LIST;
	}

	const stored = config?.presentation;

	return Object.values(FishbowlPresentation).includes(
		stored as FishbowlPresentation,
	)
		? (stored as FishbowlPresentation)
		: FishbowlPresentation.LIST;
}
